import type { Patient } from "services/types/patients.type";

import { popCallerName } from "../incoming-call-pop.slice";
import useIncomingCallPatients from "../use-incoming-call-patients.hook";
import useInboundCallPops, { isRinging } from "./use-inbound-call-pops.hook";

const patientName = (patient: Patient) =>
  patient.fullName ||
  `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim();

// Several patients can share a number; the list is appointment-sorted, so the
// likeliest caller leads.
export function patientsLabel(patients: Patient[]) {
  const names = patients.map(patientName).filter(Boolean);
  if (names.length <= 2) return names.join(" or ");
  return `${names[0]} or ${names.length - 1} others`;
}

// Who Suno knows each ringing call as, by SDK call id: the matched patient(s),
// else the Rolodex contact, else the pop's caller ID. A call with no pop, or
// one whose patients are still loading, has no entry (or its caller ID) until
// the match lands. One patient fetch covers every ringing call; with a single
// call it is the same request the pop card makes.
export default function useInboundCallerNames(): Record<string, string> {
  const ringing = useInboundCallPops().filter(({ call }) => isRinging(call));
  const patientIds = [
    ...new Set(ringing.flatMap(({ pop }) => pop?.patientIds ?? []))
  ];
  const { patients } = useIncomingCallPatients(patientIds);

  const names: Record<string, string> = {};
  for (const { callId, pop } of ringing) {
    if (!pop) continue;
    const name =
      patientsLabel(
        patients.filter((patient) => pop.patientIds.includes(patient.id))
      ) ||
      pop.contact?.name ||
      popCallerName(pop);
    if (name) names[callId] = name;
  }
  return names;
}
